import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Building2, MapPin } from "lucide-react";
import { HUBS } from "@/data/hubs";
import { ogImageMeta } from "@/lib/og-image";
import { leafCanonical } from "@/lib/canonical";

const URL_SELF = "https://vendingmachinefree.com/hubs";
const TITLE = "Corporate Tech Hubs — Free Vending & Micro-Markets | Vending Machine Free";
const DESC =
  "Free vending machines and micro-markets across America's biggest corporate & tech hubs — DFW Legacy Park, Frisco Platinum Corridor, Las Colinas, Silicon Beach, DTLA, Silicon Valley, and San Francisco SOMA / Financial District.";

export const Route = createFileRoute("/hubs")({
  head: ({ match, matches }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL_SELF },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      ...ogImageMeta(),
    ],
    links: leafCanonical(URL_SELF, { match, matches }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Corporate & Tech Hubs — Free Vending Coverage",
          url: URL_SELF,
          hasPart: HUBS.map((h) => ({
            "@type": "Place",
            name: h.name,
            address: { "@type": "PostalAddress", addressLocality: h.city },
          })),
        }),
      },
    ],
  }),
  component: HubsIndex,
});

function HubsIndex() {
  const grouped = HUBS.reduce<Record<string, typeof HUBS>>((acc, h) => {
    (acc[h.region] ??= []).push(h);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <Link
            to="/"
            hash="contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x max-w-4xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            <Building2 className="h-4 w-4" /> Corporate & Tech Hubs
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase">
            Free Vending & Micro-Markets — America's Corporate Tech Hubs
          </h1>
          <p className="mt-4 text-white/80 text-lg">
            We place vending machines, smart fridges, and full micro-markets at zero cost inside the
            densest corporate & tech corridors in the country. Pick your hub below or apply directly.
          </p>
        </div>
      </section>

      {Object.entries(grouped).map(([region, hubs]) => (
        <section key={region} className="py-14 even:bg-secondary">
          <div className="container-x max-w-6xl">
            <h2 className="font-display text-2xl uppercase">{region}</h2>
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {hubs.map((h) => (
                <Link
                  key={h.slug}
                  to="/hubs/$hub"
                  params={{ hub: h.slug }}
                  className="group rounded-xl border border-border bg-card p-6 hover:border-safety hover:shadow-md transition"
                >
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                    <MapPin className="h-4 w-4 text-safety" /> {h.city}
                  </div>
                  <h3 className="mt-2 font-display text-lg uppercase group-hover:text-safety">
                    {h.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{h.intro}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-safety">
                    View hub <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="py-16 text-center">
        <div className="container-x">
          <h2 className="font-display text-3xl uppercase">
            Don't See Your Hub? We Cover All 50 States
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            Our operator network runs free-placement vending, smart-fridge, and micro-market service
            nationwide. Apply and we'll match you to a vetted local operator within 24 hours.
          </p>
          <Link
            to="/"
            hash="contact"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
